/*eslint-disable*/
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import Notification from "../../@core/components/notifications"
import { useTranslation } from "react-i18next"
import { auth } from "../../api/auth"
import { USER_TYPES } from "../../resources/enums"
import { validateEmail } from "../../api/parameters"
import { getByItaCode } from "../../api/ita"
import { userContext } from "../../context/user"
import { startVerification } from "../../core/infrastructure/security/deviceVerification"
import { startOtpPending } from "../../core/infrastructure/security/otpPending"

// FOC-492 — Callbacks opcionales que la pantalla de login puede pasar para
// manejar los códigos nuevos del backend. Si no se pasan, el error se
// muestra con el toast genérico (compatibilidad hacia atrás).
// FOC-491 AC1 — onOtpRequired dispara cuando el back cortó en /authentication
// con otpRequired=true (user tiene OTP y falta validarlo).
const useLogin = (callbacks) => {
	const { t } = useTranslation()
	const { customNotification } = Notification()
	const { setUser, setToken } = userContext()
	const navigate = useNavigate()
	const [isLoading, setIsLoading] = useState(false)
	const [openModalItaCode, setOpenModalItaCode] = useState(false)
	const [userInfoTemp, setUserInfoTemp] = useState(false)
	const [userEmployeeTemp, setUserEmployeeTemp] = useState(false)
	const [itaCode, setItaCode] = useState("")

	const [formData, setFormData] = useState()

	const toggleModal = () => {
		setOpenModalItaCode(!openModalItaCode)
	}

	// FOC-492 — Lógica común usada por el login normal, por el verify del
	// dispositivo nuevo (/device/verify) y por la validación de OTP
	// (/otp/validate). Los tres caminos devuelven el mismo shape de payload
	// desenvuelto: { user, token, ibo, allowed?, menu?, ... }.
	//
	// Si el user es Employee y su email está en el parámetro LoginBlockpay,
	// abrimos el ModalItaCode para que decida: entrar como empleado o entrar
	// con el código de un IBO. En caso contrario, sesión directa.
	//
	// Cuando llegamos aquí NO hay que volver a chequear enabledOtp: si el
	// user tenía 2FA, el back ya respondió con otpRequired=true en el paso
	// de /authentication y el OTP se validó en /otp/validate antes de
	// devolver este payload.
	const handleAuthData = async (payload, email) => {
		const user = payload?.user
		const token = payload?.token
		const ibo = payload?.ibo
		if (!user || !token) {
			customNotification(t("Error"), t("LoginError"), "danger")
			return
		}
		localStorage.setItem("tokenBP", token)

		const effectiveEmail = email ?? user.email ?? ""

		if (user?.userTypeId === USER_TYPES.EMPLOYEE) {
			const validEmail = await validateEmail("LoginBlockpay")
			const emails = validEmail?.[0]?.value?.split(",") ?? []
			const isAvailable = emails.some((item) => item === effectiveEmail)
			if (isAvailable) {
				localStorage.setItem("userEmployee", JSON.stringify(user))
				setUserInfoTemp(user)
				setUserEmployeeTemp({ ...user, token })
				setOpenModalItaCode(true)
				return
			}
			// Employee NO en la lista: entra como empleado directo (mismo
			// tratamiento que un IBO regular).
		}

		localStorage.setItem("userInfo", JSON.stringify({ ...user, ...ibo }))
		setUser({ ...user, ...ibo })
		navigate("/profile")
	}

	const signIn = async (values) => {
		setIsLoading(true)

		const response = await auth({
			...formData,
			email: values.email,
			password: values.password,
			iboCode: 0
		})

		// FOC-477 — Clave vencida: back responde 403 con error PASSWORD_EXPIRED
		// y un tempToken (scope "password-change"). Guardamos los datos en
		// sessionStorage y navegamos al flujo de cambio obligatorio.
		const errData = response?.data?.data
		if (
			response?.status === 403 &&
			errData?.error === "PASSWORD_EXPIRED" &&
			errData?.tempToken
		) {
			localStorage.removeItem("userInfo")
			localStorage.removeItem("tokenBP")
			sessionStorage.setItem("tempToken", errData.tempToken)
			sessionStorage.setItem("emailMasked", errData.emailMasked ?? "")
			sessionStorage.setItem(
				"tempTokenExpiresAt",
				String(Date.now() + ((errData.expiresInMinutes ?? 15) * 60 * 1000))
			)
			navigate("/change-password-required")
			setIsLoading(false)
			return
		}

		// FOC-492 — Tres códigos nuevos del login que requieren acciones
		// específicas de la pantalla.
		if (response?.status === 403) {
			const code = response?.data?.code

			if (code === "DEVICE_VERIFICATION_REQUIRED") {
				const payload = response?.data?.data
				if (payload?.verificationToken && payload?.maskedEmail) {
					// Limpiar residuos de sesión previa para que getUserToken()
					// caiga al verificationToken (sin esto, pisaríamos el fallback).
					localStorage.removeItem("userInfo")
					localStorage.removeItem("userInfoTFA")
					localStorage.removeItem("userEmployee")
					localStorage.removeItem("tokenBP")
					startVerification({
						verificationToken: payload.verificationToken,
						maskedEmail: payload.maskedEmail,
						expiresIn: Number(payload.expiresIn ?? 900),
						resendAvailableAt: payload.resendAvailableAt
					})
					callbacks?.onDeviceVerificationRequired?.(payload)
					setIsLoading(false)
					return
				}
			}

			if (code === "ACCOUNT_BLOCKED") {
				callbacks?.onAccountBlocked?.()
				setIsLoading(false)
				return
			}

			if (code === "DEVICE_VERIFICATION_UNAVAILABLE") {
				callbacks?.onVerificationUnavailable?.()
				setIsLoading(false)
				return
			}
		}

		if (response?.status === 200) {
			const payload = response?.data?.data

			// FOC-491 AC1 — Si el back cortó por OTP, no hay sesión todavía.
			// Guardamos el otp_pending y navegamos al step 2 (/login-validate).
			if (payload?.otpRequired && payload?.otpPendingToken) {
				localStorage.removeItem("userInfo")
				localStorage.removeItem("userInfoTFA")
				localStorage.removeItem("userEmployee")
				localStorage.removeItem("tokenBP")
				startOtpPending({
					otpPendingToken: payload.otpPendingToken,
					maskedEmail: payload.maskedEmail ?? "",
					expiresIn: 15 * 60
				})
				if (callbacks?.onOtpRequired) {
					callbacks.onOtpRequired({
						otpPendingToken: payload.otpPendingToken,
						maskedEmail: payload.maskedEmail ?? "",
						expiresIn: 15 * 60
					})
				} else {
					navigate("/login-validate")
				}
				setIsLoading(false)
				return
			}

			await handleAuthData(payload, values.email)
		} else {
			customNotification(
				t("Error"),
				response?.data?.message ?? t("LoginError"),
				"danger"
			)
		}
		setIsLoading(false)
	}

	const logout = () => {
		localStorage.removeItem("userInfo")
		localStorage.removeItem("userInfoTFA")
		localStorage.removeItem("userEmployee")
		localStorage.removeItem("tokenBP")
		navigate("/login")
	}

	const handleChange = (e) => {
		const { name, value } = e.target

		setFormData({
			...formData,
			[name]: value
		})
	}

	// ** Entrada de un empleado con el código de IBO de otra persona.
	//
	// La sesión que queda abierta es la del IBO, no la del empleado: a partir de aquí la
	// aplicación trabaja con sus cuentas, sus movimientos y su cupo.
	//
	// FOC-491 AC1 — Ya NO chequeamos enabledOtp acá: si el empleado tenía
	// 2FA, el OTP se validó en /otp/validate antes de abrir el modal. El
	// IBO destino no re-pide OTP (no hay re-autenticación en este cambio).
	const signInWithItaCode = async (values) => {
		const ibo = await getByItaCode(values.itaCode)
		if (ibo && ibo?.id) {
			const session = {
				userId: ibo.userId,
				affiliationDate: ibo.creationDate,
				userTypeId: 1,
				name: ibo.name,
				lastName: ibo.lastName,
				email: ibo.email,
				imageUrl: ibo.imageUrl,
				code: ibo.code,
				otpEnabled: true,
				renewalDate: ibo.renewalDate
			}

			localStorage.setItem("userInfo", JSON.stringify(session))
			setUser(session)
			navigate("/")
		} else {
			customNotification(t("Error"), t("Código de ibo no encontrado"), "danger")
		}
	}

	// FOC-491 AC1 — Idem: el empleado ya validó su OTP (si tenía) antes.
	const signInAsEmployee = () => {
		localStorage.setItem("userInfo", JSON.stringify(userInfoTemp))
		setUser(userInfoTemp)
		setToken(userInfoTemp.token)
		navigate("/profile")
	}

	// FOC-492 — handleAuthData se exporta para que la pantalla de verificación
	// de dispositivo y /login-validate puedan llamar la misma lógica después
	// de un verify / validate OK.
	return {
		signIn,
		handleChange,
		logout,
		isLoading,
		openModalItaCode,
		setItaCode,
		signInWithItaCode,
		toggleModal,
		signInAsEmployee,
		setOpenModalItaCode,
		handleAuthData
	}
}

export default useLogin
